/**
 * Dynamic grid `conditionalFields` — **FilterDependentData** contract:
 *
 * - **`Column`**: Control column (e.g. `InstrumentName`). When the user picks a value here,
 *   that choice drives which options appear in dependent dropdowns.
 * - **`DependentFilteredFields`**: Each entry targets one dependent column:
 *   - **`Field`**: `columnKey` of the column whose **`jsonOptionData`** list should be filtered.
 *   - **`FilterOn`**: Property name present on **both** the control option objects and each
 *     dependent option object. Dependent options are kept when
 *     `dependentOption[FilterOn] === controlFilterValue`, where `controlFilterValue` is taken from
 *     the **selected control option**’s `FilterOn` (preferred), or from `row[FilterOn]` if set
 *     on the row (e.g. hidden id column).
 *
 * Example: control `InstrumentName` options like `{ value, label, InstrumentId }`; dependent
 * `TestName` options like `{ value, label, InstrumentId }` — filter with `FilterOn: "InstrumentId"`.
 */

export type FieldOptionSource = {
  name?: string;
  columnKey?: string;
  jsonOptionData?: string | null;
};

const norm = (v: any) => String(v ?? "").trim().toLowerCase();

export function isFilterDependentDataAction(cfg: any): boolean {
  return norm(cfg?.ActionType) === norm("FilterDependentData");
}

export function isShowHideAction(cfg: any): boolean {
  return norm(cfg?.ActionType) === norm("ShowHide");
}

export function isShowSelectedValueAction(cfg: any): boolean {
  return norm(cfg?.ActionType) === norm("ShowSelectedValue");
}

export function parseJsonOptionsArray(raw: string | null | undefined): any[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

/** Match option.value to row value (string/number tolerant). */
export function optionValueMatchesRow(
  optionValue: any,
  rowValue: any
): boolean {
  if (rowValue === undefined || rowValue === null || rowValue === "") {
    return false;
  }
  return String(optionValue ?? "") === String(rowValue ?? "");
}

/**
 * Value used to filter dependent `jsonOptionData` rows (e.g. `InstrumentId`).
 *
 * 1. Prefer the **selected option** for `controllingColumn` (`Column`): find the option whose
 *    `value` equals `row[controllingColumn]`, then read `option[FilterOn]`.
 * 2. Else use **`row[FilterOn]`** when the row stores the id (or other key) explicitly.
 */
export function resolveFilterOnValue(
  row: Record<string, any> | null | undefined,
  filterOn: string | undefined,
  controllingColumn: string | undefined,
  fieldSources: FieldOptionSource[] | null | undefined
): string | number | undefined {
  if (!filterOn) return undefined;

  if (controllingColumn && fieldSources?.length) {
    const src = fieldSources.find(
      (f) => f?.name === controllingColumn || f?.columnKey === controllingColumn
    );
    const options = parseJsonOptionsArray(src?.jsonOptionData as string);
    const selected = row?.[controllingColumn];
    const match = options.find((opt: any) =>
      optionValueMatchesRow(opt?.value, selected)
    );
    if (match && Object.prototype.hasOwnProperty.call(match, filterOn)) {
      const v = match[filterOn];
      if (v !== undefined && v !== null && v !== "") {
        return v;
      }
    }
  }

  const direct = row?.[filterOn];
  if (direct !== undefined && direct !== null && direct !== "") {
    return direct;
  }

  return undefined;
}

export function findFilterDependentConfigForColumn(
  conditionalFields: any[] | null | undefined,
  dependentColumnKey: string
): { cfg: any; rule: any } | null {
  if (!conditionalFields?.length || !dependentColumnKey) return null;
  for (const cfg of conditionalFields) {
    if (!isFilterDependentDataAction(cfg)) continue;
    const deps = cfg?.DependentFilteredFields;
    if (!Array.isArray(deps)) continue;
    const rule = deps.find((d: any) => d?.Field === dependentColumnKey);
    if (rule) return { cfg, rule };
  }
  return null;
}

export function filterOptionsByDependentData(
  options: any[],
  dependentColumnKey: string,
  conditionalFields: any[] | null | undefined,
  row: Record<string, any> | null | undefined,
  fieldSources: FieldOptionSource[] | null | undefined
): any[] {
  if (!Array.isArray(options) || !options.length) return [];
  const found = findFilterDependentConfigForColumn(
    conditionalFields,
    dependentColumnKey
  );
  if (!found) return options;

  const { cfg, rule } = found;
  const filterOn = rule?.FilterOn;
  const controllingColumn = cfg?.Column;
  const filterValue = resolveFilterOnValue(
    row || {},
    filterOn,
    controllingColumn,
    fieldSources
  );

  if (
    filterValue === undefined ||
    filterValue === null ||
    filterValue === ""
  ) {
    return [];
  }

  return options.filter(
    (opt: any) => String(opt?.[filterOn] ?? "") === String(filterValue ?? "")
  );
}

export function getDependentFieldKeysForControlColumn(
  conditionalFields: any[] | null | undefined,
  controlColumn: string
): string[] {
  if (!conditionalFields?.length || !controlColumn) return [];
  const keys: string[] = [];
  for (const cfg of conditionalFields) {
    if (!isFilterDependentDataAction(cfg)) continue;
    if (cfg?.Column !== controlColumn) continue;
    const deps = cfg?.DependentFilteredFields;
    if (!Array.isArray(deps)) continue;
    deps.forEach((d: any) => {
      if (d?.Field) keys.push(d.Field);
    });
  }
  return keys;
}

/** Build a row-like object from grid search filters + tab headers. */
export function rowFromSearchFilters(
  filters: any[] | null | undefined,
  tabHeaders: any[] | null | undefined
): Record<string, any> {
  const row: Record<string, any> = {};
  if (!filters?.length || !tabHeaders?.length) return row;
  for (const f of filters) {
    const col = tabHeaders.find(
      (c: any) => c?.filterColumns === f?.columnName
    );
    if (col?.columnKey != null) row[col.columnKey] = f.filterValue;
  }
  return row;
}
